import {
  noticeIsDrawnIn,
  noticeWorkAreaId,
  waitingNoticesInScope,
  type NoticePlaceType,
  type WaitingNoticeCountsType,
} from '@featit/shared/notification-destination';
import { type INotification } from '@featit/shared/types/index';
import { priorityRank } from '@featit/shared/work-items';
import type { ITask } from '@/services/TasksApi';

/**
 * F-0744 (`docs/concept-notifications.md` §No bell): which of the reader's notices a Decisions or
 * Tasks list in this scope draws, and how many. One rule, read by the lists and by their badges, so
 * the two cannot disagree (as `decisionsWaitingInScope` and `tasksWaitingInScope` are).
 */

export type { NoticePlaceType };
export { noticeWorkAreaId };

/** What `NotificationStore` has to answer for this rule; the store itself satisfies it. */
export interface INoticesSource {
  notifications: readonly INotification[];
  noticesRead: boolean;
  /** I-2382: the server's count of every waiting notice; absent or null from an older server. */
  waiting?: WaitingNoticeCountsType | null;
}

export interface INoticesScope {
  allWorkAreas: boolean;
  workAreaId: string | null;
  namespaceId?: string | null;
}

/** The namespace a work area is in, as the loaded work areas know it. */
export type NamespaceOfType = (workAreaId: string) => string | null | undefined;

/**
 * A notice about an item, drawn as a row of the list itself (AC-NB4); every other one is a *For you*
 * row, having no item to choose or open beside the list.
 */
export function isItemNotice(notice: INotification): boolean {
  return Boolean(notice.workItemId && notice.workItem?.key && notice.workItem.project?.id);
}

/**
 * Whether a notice is in the scope on screen. One about the person — a session, a credential, the
 * plan — is in every scope: they have to find it wherever they look.
 */
export function noticeInScope(
  notice: INotification,
  scope: INoticesScope,
  namespaceOf: NamespaceOfType,
): boolean {
  const workAreaId = noticeWorkAreaId(notice);
  return workAreaId === null || workAreaInScope(workAreaId, scope, namespaceOf);
}

/** Whether a work area is in the scope on screen: all of them, its namespace, or that one. */
function workAreaInScope(
  workAreaId: string,
  scope: INoticesScope,
  namespaceOf: NamespaceOfType,
): boolean {
  if (scope.allWorkAreas) return true;
  if (scope.namespaceId) return namespaceOf(workAreaId) === scope.namespaceId;
  return scope.workAreaId === workAreaId;
}

/** The notices a list in this place and scope draws: unread, and marked read on this visit. */
export function noticesIn(
  source: INoticesSource | undefined,
  place: NoticePlaceType,
  scope: INoticesScope,
  namespaceOf: NamespaceOfType,
): INotification[] {
  // A surface drawn without the notices (a store-less embed, a test) draws none.
  if (!source) return [];
  return source.notifications.filter(
    (notice) => noticeIsDrawnIn(notice, place) && noticeInScope(notice, scope, namespaceOf),
  );
}

/**
 * How many waiting notices a badge in this place and scope adds; 0 until the list has been read.
 *
 * I-2382 (AC-NB17): the server's count of every waiting notice in the scope, less those marked read
 * on this visit, never the length of the page the list holds. Jay's badge read 23 tasks + 83, the
 * 83 being what a page of 100 held; across All work areas it read a flat 100.
 */
export function noticesWaitingInScope(
  source: INoticesSource | undefined,
  place: NoticePlaceType,
  scope: INoticesScope,
  namespaceOf: NamespaceOfType,
): number {
  if (!source?.noticesRead) return 0;
  const drawn = noticesIn(source, place, scope, namespaceOf);
  if (!source.waiting) return drawn.filter((notice) => !notice.isRead).length;
  const waiting = waitingNoticesInScope(source.waiting[place], (workAreaId) =>
    workAreaInScope(workAreaId, scope, namespaceOf),
  );
  return Math.max(0, waiting - drawn.filter((notice) => notice.isRead).length);
}

/**
 * A list's count with its notices (AC-NB5): unknown while the list's own count is, never a second
 * number beside it.
 */
export function withNotices(count: number | undefined, notices: number): number | undefined {
  return count === undefined ? undefined : count + notices;
}

/** An item notice as a row of Tasks for me: its item, the notice's kind as its reason, its age. */
export function noticeTask(notice: INotification): ITask {
  const item = notice.workItem!;
  return {
    item: {
      id: notice.workItemId!,
      workAreaId: item.project!.id,
      key: item.key,
      title: item.title,
      priority: null,
    },
    reason: 'notice',
    reasonLabel: notice.type,
    waitingSince: notice.createdAt,
    hold: null,
    notice,
  };
}

/**
 * Tasks and item notices in one list, by the list's own rule: urgent first, then oldest waiting
 * (`taskWaitOrder`). A notice carries no priority and ranks as an item with none.
 */
export function withNoticeRows(
  tasks: readonly ITask[],
  notices: readonly INotification[],
): ITask[] {
  const rows = notices.filter(isItemNotice).map(noticeTask);
  if (rows.length === 0) return [...tasks];
  return [...tasks, ...rows].sort((a, b) => {
    const rank = priorityRank(a.item.priority) - priorityRank(b.item.priority);
    if (rank !== 0) return rank;
    return Date.parse(a.waitingSince) - Date.parse(b.waitingSince);
  });
}
